import type { MapSchema } from "@colyseus/schema"
import type Player from "../../models/colyseus-models/player"
import type { Pokemon } from "../../models/colyseus-models/pokemon"
import type GameRoom from "../../rooms/game-room"
import type GameState from "../../rooms/states/game-state"
import type { IPokemonEntity } from "../../types"
import type { Ability } from "../../types/enum/Ability"
import type { EffectEnum } from "../../types/enum/Effect"
import type { AttackType } from "../../types/enum/Game"
import type { Item } from "../../types/enum/Item"
import type { Passive } from "../../types/enum/Passive"
import type { Board } from "../board"
import type { PokemonEntity } from "../pokemon-entity"
import type Simulation from "../simulation"

type EffectOrigin = EffectEnum | Item | Passive | Ability

export abstract class Effect {
  priority: number = 0
  origin?: EffectOrigin
  apply(...args: any[]) {}
  constructor(effect?: (...args: any[]) => void, origin?: EffectOrigin) {
    if (effect) {
      this.apply = effect
    }
    this.origin = origin
  }
}

// applied on fight start or when spawning
export class OnSpawnEffect extends Effect {
  constructor(
    effect?: (
      entity: PokemonEntity,
      player?: Player,
      isSpawn?: boolean
    ) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  override apply(entity: PokemonEntity, player?: Player, isSpawn?: boolean) {}
}

// effect applied when consuming a dish, either at the start of the fight or when eating a dish on the bench
interface OnDishConsumedEffectArgs {
  pokemon: Pokemon
  dish: Item
  player?: Player
  entity?: PokemonEntity
}
export class OnDishConsumedEffect extends Effect {
  constructor(effect?: (args: OnDishConsumedEffectArgs) => void) {
    super(effect)
  }
  override apply(args: OnDishConsumedEffectArgs) {}
}

// item effect applied on fight start of after stealing/obtaining an item
export class OnItemGainedEffect extends Effect {
  constructor(effect?: (pokemon: PokemonEntity, item: Item) => void) {
    super(effect)
  }
  override apply(pokemon: PokemonEntity, item: Item) {}
}

export class OnItemRemovedEffect extends Effect {
  constructor(effect?: (pokemon: PokemonEntity, item: Item) => void) {
    super(effect)
  }
  override apply(pokemon: PokemonEntity, item: Item) {}
}

interface OnStageStartEffectArgs {
  player: Player
  pokemon?: Pokemon
  room: GameRoom
}

// applied in between rounds at the start of the picking phase
export class OnStageStartEffect extends Effect {
  constructor(
    effect?: (args: OnStageStartEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: OnStageStartEffectArgs) {}
}

// applied when pokemon is moved to another position on the board during pick phases

interface OnChangePositionEffectArgs {
  pokemon: Pokemon
  player: Player
  state?: GameState // can be undefined for bots updatePlayerTeam method
  oldX: number
  oldY: number
  newX: number
  newY: number
}

export class OnChangePositionEffect extends Effect {
  constructor(
    effect?: (args: OnChangePositionEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: OnChangePositionEffectArgs) {}
}

// applied after evolution

export class OnEvolutionEffect extends Effect {
  constructor(
    effect?: (args: { pokemonEvolved: Pokemon; player: Player }) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: { pokemonEvolved: Pokemon; player: Player }) {}
}

// applied when a pokemon enters or leaves the spotlight

export class OnSpotlightChangeEffect extends Effect {
  constructor(
    effect?: (args: {
      pokemon: Pokemon
      player: Player
      inSpotlight: boolean
    }) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: { pokemon: Pokemon; player: Player; inSpotlight: boolean }) {}
}

// applied when a pokemon is benched during a fight
interface OnBenchedDuringFightEffectArgs {
  pokemon: Pokemon
  player: Player
  simulation: Simulation
}

export class OnBenchedDuringFightEffect extends Effect {
  constructor(
    effect?: (args: OnBenchedDuringFightEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: OnBenchedDuringFightEffectArgs) {}
}

interface OnSimulationStartEffectArgs {
  simulation: Simulation
  player?: Player
  team: MapSchema<IPokemonEntity>
  entity: PokemonEntity
}

// applied after simulation started, when the board is fully set up
export class OnSimulationStartEffect extends Effect {
  constructor(
    effect?: (args: OnSimulationStartEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
  apply(args: OnSimulationStartEffectArgs) {}
}

interface OnItemDroppedEffectArgs {
  pokemon: Pokemon
  player: Player
  item: Item
  room: GameRoom
}

// called when an item is dragged to a pokemon ; return false to prevent equipping the item
export class OnItemDroppedEffect extends Effect {
  apply(args: OnItemDroppedEffectArgs): boolean {
    return true
  }
  constructor(
    effect?: (args: OnItemDroppedEffectArgs) => boolean,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

export interface OnKillEffectArgs {
  attacker: PokemonEntity
  target: PokemonEntity
  board: Board
  attackType: AttackType
}

// applied after knocking out an enemy
export class OnKillEffect extends Effect {
  apply(args: OnKillEffectArgs) {}
  constructor(
    effect?: (args: OnKillEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied on KO (does not proc if resurrection)
export interface OnDeathEffectArgs {
  board: Board
  pokemon: PokemonEntity
  attacker: PokemonEntity | null
}

export class OnDeathEffect extends Effect {
  apply(args: OnDeathEffectArgs) {}
  constructor(
    effect?: (args: OnDeathEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

export class OnResurrectEffect extends Effect {
  apply(args: OnDeathEffectArgs) {}
  constructor(
    effect?: (args: OnDeathEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

export class PeriodicEffect extends Effect {
  intervalMs: number
  timer: number
  count: number

  constructor(
    effect: (entity: PokemonEntity, board: Board, ...others: any[]) => void,
    origin: EffectOrigin,
    intervalMs: number
  ) {
    super(effect, origin)
    this.intervalMs = intervalMs
    this.timer = intervalMs
    this.count = 0
  }

  update(dt: number, entity: PokemonEntity, board: Board) {
    this.timer -= dt
    if (this.timer <= 0) {
      this.count++
      this.apply(entity, board)
      this.timer = this.intervalMs
    }
  }
}

interface OnHitEffectArgs {
  attacker: PokemonEntity
  target: PokemonEntity
  board: Board
  totalTakenDamage: number
  physicalDamage: number
  specialDamage: number
  trueDamage: number
}

// applied after every successful basic attack (not dodged or protected)
export class OnHitEffect extends Effect {
  apply(params: OnHitEffectArgs) {}
  constructor(
    effect?: (params: OnHitEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

interface OnAttackEffectArgs {
  pokemon: PokemonEntity
  target: PokemonEntity | null
  board: Board
  physicalDamage: number
  specialDamage: number
  trueDamage: number
  totalDamage: number
  crit: boolean
  isTripleAttack?: boolean
  hasAttackKilled?: boolean
}

export class BeforeAttackEffect extends Effect {
  override apply(args: OnAttackEffectArgs) {}
  constructor(
    effect?: (args: OnAttackEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

export class OnAttackEffect extends Effect {
  override apply(args: OnAttackEffectArgs) {}
  constructor(
    effect?: (args: OnAttackEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after casting an ability
export class OnAbilityCastEffect extends Effect {
  apply(
    pokemon: PokemonEntity,
    board: Board,
    target: PokemonEntity | null,
    crit: boolean
  ) {}
  constructor(
    effect?: (
      pokemon: PokemonEntity,
      board: Board,
      target: PokemonEntity | null,
      crit: boolean
    ) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after having received damage and not being KO

export interface OnDamageReceivedEffectArgs {
  pokemon: PokemonEntity
  attacker: PokemonEntity | null
  board: Board
  damage: number
  damageBeforeReduction: number
  attackType?: AttackType
  isRetaliation: boolean
}

export class OnDamageReceivedEffect extends Effect {
  apply(args: OnDamageReceivedEffectArgs) {}
  constructor(
    effect?: (args: OnDamageReceivedEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after having received a basic attack
export interface OnAttackReceivedEffectArgs {
  pokemon: PokemonEntity
  attacker: PokemonEntity
  board: Board
  physicalDamage: number
  specialDamage: number
  trueDamage: number
  totalDamage: number
  isTripleAttack?: boolean
  attackType?: AttackType
  crit: boolean
}

export class OnAttackReceivedEffect extends Effect {
  apply(args: OnAttackReceivedEffectArgs) {}
  constructor(
    effect?: (args: OnAttackReceivedEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after dealing damage
export interface OnDamageDealtEffectArgs {
  pokemon: PokemonEntity
  target: PokemonEntity
  damage: number
  attackType?: AttackType
  isRetaliation: boolean
}

export class OnDamageDealtEffect extends Effect {
  apply(args: OnDamageDealtEffectArgs) {}
  constructor(
    effect?: (args: OnDamageDealtEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

export class OnMoveEffect extends Effect {
  override apply(
    pokemon: PokemonEntity,
    board: Board,
    oldX: number,
    oldY: number,
    newX: number,
    newY: number
  ) {}
  constructor(
    effect?: (
      pokemon: PokemonEntity,
      board: Board,
      oldX: number,
      oldY: number,
      newX: number,
      newY: number
    ) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after taking a hit that removed all the remaining shield
interface OnShieldDepletedEffectArgs {
  pokemon: PokemonEntity
  attacker: PokemonEntity | null
  board: Board
  damage: number
}

export class OnShieldDepletedEffect extends Effect {
  override apply(args: OnShieldDepletedEffectArgs) {}
  constructor(
    effect?: (args: OnShieldDepletedEffectArgs) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}

// applied after a Ground Pokémon digs the ground between rounds

export class OnGroundDiggingEffect extends Effect {
  override apply(args: { pokemon: Pokemon; player: Player }) {}
  constructor(
    effect?: (args: { pokemon: Pokemon; player: Player }) => void,
    origin?: EffectOrigin
  ) {
    super(effect, origin)
  }
}
